/**
 * Entry point — wires AudioEngine + Renderer + UI and starts the render loop.
 */

import "./ui/styles.css";

import { AudioEngine } from "./audio/sources";
import { Renderer, type SceneType } from "./viz/renderer";
import { ALT_SCENE_LABELS, type AltSceneType } from "./viz/altScenes";
import { resolveSceneLink } from "./viz/scenes/sceneLinks";
import { BackgroundRenderer } from "./viz/background";
import { initUI } from "./ui/controls";
import {
  loadRunningParams,
  readPresetFromLocation,
  saveRunningParams,
} from "./presets/store";
import { registerPwa } from "./pwa";
import { MusicRuntime } from "./music/runtime";
import { initMusicDebugToggle, readInitialDebugReactivityMode } from "./music/debugToggle";

function bootstrap(): void {
  const canvas = document.getElementById("viz");
  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error("Missing #viz canvas element.");
  }

  const engine = new AudioEngine();
  const renderer = new Renderer(canvas);
  const params = new URLSearchParams(window.location.search);
  const musicDebugEnabled = params.get("music-debug") === "1" || params.get("fluid-debug") === "1";
  const musicRuntime = new MusicRuntime({
    mode: musicDebugEnabled ? readInitialDebugReactivityMode(params) : "legacy",
    qualityMode: "high",
    surface: "desktop",
    exposeGlobal: musicDebugEnabled,
  });

  // Restore the last-known visual params *before* the UI builds, so every
  // slider initialises on the saved value instead of a default.
  Object.assign(renderer.params, loadRunningParams());

  // Shared deep-link (?preset=<base64>) overrides the auto-saved params so
  // recipients of a share URL see the sender's exact look — vizz.fm-style.
  // We do this *after* loadRunningParams so the link wins, but *before* the
  // UI builds so every slider mounts on the shared values.
  const shared = readPresetFromLocation();
  if (shared) {
    Object.assign(renderer.params, shared.params);
  }

  const sceneParam = params.get("scene");
  const sceneLink = resolveSceneLink(sceneParam);
  const allowedScenes = new Set<SceneType>(["bioclast", ...(Object.keys(ALT_SCENE_LABELS) as AltSceneType[])]);
  if (sceneLink) {
    Object.assign(renderer.params, sceneLink.values);
    renderer.sceneType = sceneLink.sceneType;
    if (!params.has("ui")) {
      document.body.classList.add("ui-hidden");
    }
  } else if (sceneParam && allowedScenes.has(sceneParam as SceneType)) {
    renderer.sceneType = sceneParam as SceneType;
  }

  // Auto-save the running params at most once per second. The renderer mutates
  // params in-place from the UI, so we just snapshot whatever's there. If the
  // user hasn't touched anything, the write is a no-op-equivalent (same JSON).
  const PARAM_SAVE_INTERVAL_MS = 1000;
  setInterval(() => {
    saveRunningParams(renderer.params);
  }, PARAM_SAVE_INTERVAL_MS);
  // Also save on tab close so a near-instant change doesn't get lost.
  window.addEventListener("pagehide", () => saveRunningParams(renderer.params));

  const backdropEl = document.getElementById("backdrop");
  // BackgroundRenderer manages 6 styles (tint / solid / linear / radial /
  // starfield / plasma). With style="tint" it preserves the original
  // renderer-driven backdrop colour exactly.
  const background = backdropEl ? new BackgroundRenderer(backdropEl) : null;
  const hudBpm = document.getElementById("hudBpm");
  const hudBass = document.getElementById("hudBass");
  const hudMid = document.getElementById("hudMid");
  const hudTreble = document.getElementById("hudTreble");

  // Renderer reads features from the engine on every frame, and we mirror the
  // scene's backdrop tint + a few feature numbers into the DOM so the HUD and
  // the atmospheric background pulse with the music alongside the canvas.
  const tick = (): void => {
    const f = engine.read();
    musicRuntime.updateFromLegacy(f, { source: engine.info() });
    renderer.setFeatures(f);

    if (backdropEl) {
      const bd = renderer.backdrop;
      if (background) {
        background.tick(f, bd);
      } else {
        backdropEl.style.background = `radial-gradient(ellipse at 50% 45%, rgba(${bd.r}, ${bd.g}, ${bd.b}, ${bd.alpha.toFixed(3)}) 0%, #010104 70%, #000 100%)`;
        backdropEl.style.opacity = String(Math.min(1, bd.alpha + 0.25));
        backdropEl.style.filter = `blur(${bd.blurPx}px)`;
      }
    }

    if (hudBpm) {
      hudBpm.textContent = f.bpm > 0 ? Math.round(f.bpm).toString() : "---";
      hudBpm.classList.toggle("beat", f.beat > 0.4);
    }
    if (hudBass) hudBass.style.setProperty("--lvl", `${Math.round(f.bass * 100)}%`);
    if (hudMid) hudMid.style.setProperty("--lvl", `${Math.round(f.mid * 100)}%`);
    if (hudTreble)
      hudTreble.style.setProperty("--lvl", `${Math.round(f.treble * 100)}%`);

    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  renderer.start();
  initUI({ engine, renderer, background });
  initMusicDebugToggle({ enabled: musicDebugEnabled, runtime: musicRuntime, surface: "desktop" });

  // Optional ?ui=hidden or ?ui=0 URL flag — handy for clean wall/OBS output.
  if (params.get("ui") === "hidden" || params.get("ui") === "0") {
    document.body.classList.add("ui-hidden");
  } else if (params.get("ui") === "show" || params.get("ui") === "1") {
    document.body.classList.remove("ui-hidden");
  }

  // Surface unhandled errors in the source-status line, never silently fail.
  window.addEventListener("error", (ev) => {
    console.error(ev.error ?? ev.message);
  });
  window.addEventListener("unhandledrejection", (ev) => {
    console.error(ev.reason);
  });
}

bootstrap();
registerPwa();
